import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { usePets } from '../../context/PetsContext';
import bookingsApi from '../../../../api/bookings';
import type { Pet } from '../../types/pet';
import { PetSelection } from './PetSelection';
import { CategorySelection } from './CategorySelection';
import { ServiceSelection } from './ServiceSelection';
import { ProfessionalDirectory } from './ProfessionalDirectory';
import { ServiceDetails } from './ServiceDetails';
import { TimeSelection } from './TimeSelection';
import { DataSharingConfirmation } from './DataSharingConfirmation';
import { BookingSummary } from './BookingSummary';
import { BookingConfirmation } from './BookingConfirmation';
import { PaymentMethodSelection } from './PaymentMethodSelection';
import { CardPaymentCheckout } from './CardPaymentCheckout';
import { PaymentSuccess } from './PaymentSuccess';
import { PaymentFailure } from './PaymentFailure';

interface BookingFlowProps {
  onClose: () => void;
  initialPet?: Pet;
  initialCategory?: string;
  selectedDate?: Date | null;
}

export type ServiceCategory =
  | 'veterinary'
  | 'grooming'
  | 'boarding'
  | 'training'
  | 'walking'
  | 'breeding'
  | 'retail'
  | 'nutrition'
  | 'transportation'
  | 'insurance'
  | 'identification'
  | 'endoflife'
  | 'rescue'
  | 'events'
  | 'education';

export interface BookingData {
  selectedPets: string[];
  category: ServiceCategory | null;
  selectedServices: string[];
  professional: Record<string, unknown> | null;
  serviceDetails: Record<string, unknown>;
  appointmentTime: Date | string | null;
  sharedData: string[];
  accessDuration: string;
}

export function BookingFlow({
  onClose,
  initialPet,
  initialCategory,
  selectedDate,
}: BookingFlowProps) {
  const { i18n } = useTranslation();
  const { user } = useUser();
  const { pets } = usePets();
  const isItalian = (i18n.resolvedLanguage || i18n.language || '')
    .toLowerCase()
    .startsWith('it');
  const tr = (en: string, it: string) => (isItalian ? it : en);
  const [currentStep, setCurrentStep] = useState<
    | 'pets'
    | 'category'
    | 'service-selection'
    | 'directory'
    | 'service'
    | 'time'
    | 'data'
    | 'summary'
    | 'payment-method'
    | 'payment-checkout'
    | 'payment-success'
    | 'payment-failure'
    | 'confirmation'
  >('pets');
  const [bookingData, setBookingData] = useState<BookingData>({
    selectedPets: initialPet ? [initialPet.id] : [],
    category: (initialCategory as ServiceCategory) || null,
    selectedServices: [],
    professional: null,
    serviceDetails: {},
    appointmentTime: selectedDate || null,
    sharedData: [],
    accessDuration: 'appointment-only',
  });
  const [paymentError, setPaymentError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdBookingId, setCreatedBookingId] = useState<string | null>(null);

  // Price is determined by the professional — show 0 until confirmed
  const totalAmount = 0;

  const updateBookingData = <K extends keyof BookingData>(
    key: K,
    value: BookingData[K]
  ) => {
    setBookingData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    const stepOrder: Array<typeof currentStep> = [
      'pets',
      'category',
      'service-selection',
      'directory',
      'service',
      'time',
      'data',
      'summary',
      'payment-method',
      'confirmation',
    ];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const stepOrder: Array<typeof currentStep> = [
      'pets',
      'category',
      'service-selection',
      'directory',
      'service',
      'time',
      'data',
      'summary',
      'payment-method',
      'payment-checkout',
    ];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get the first selected pet's full data
      const firstPetId = bookingData.selectedPets[0];
      const petData = pets.find((p) => p.id === firstPetId);

      // Format scheduled date as YYYY-MM-DD
      const appt = bookingData.appointmentTime as {
        date: Date;
        time: string;
      } | null;
      let scheduledDate = '';
      if (appt?.date) {
        const d = appt.date;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        scheduledDate = `${yyyy}-${mm}-${dd}`;
      }

      // Convert 12h time (e.g. "09:00 AM") to 24h "HH:MM"
      let scheduledTime = '';
      if (appt?.time) {
        const match = appt.time.match(
          /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
        );
        if (match) {
          let hours = parseInt(match[1], 10);
          const minutes = match[2];
          const period = match[3].toUpperCase();
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          scheduledTime = `${String(hours).padStart(2, '0')}:${minutes}`;
        } else {
          // Fallback: use as-is if already in HH:MM format
          scheduledTime = appt.time;
        }
      }

      // Build owner name from Clerk user
      const ownerName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown'
        : 'Unknown';
      const ownerEmail =
        user?.primaryEmailAddress?.emailAddress || undefined;

      // Build the professional reference
      const professional = bookingData.professional as Record<
        string,
        unknown
      > | null;

      const result = await bookingsApi.create({
        businessId: (professional?.id as string) || 'unknown',
        petId: petData?.id,
        petName: petData?.name || 'Unknown Pet',
        petSpecies: petData?.species,
        petBreed: petData?.breed,
        ownerName,
        ownerEmail,
        scheduledDate,
        scheduledTime,
        serviceType: bookingData.category || undefined,
        serviceName:
          bookingData.selectedServices.length > 0
            ? bookingData.selectedServices.join(', ')
            : undefined,
        serviceCategory: bookingData.category || undefined,
        notes:
          (bookingData.serviceDetails?.notes as string) || undefined,
        isEmergency:
          (bookingData.serviceDetails?.isEmergency as boolean) ||
          false,
        dataShared:
          bookingData.sharedData.length > 0
            ? bookingData.sharedData
            : undefined,
      });

      // Store the created booking ID for the confirmation screen
      setCreatedBookingId(result.booking.id);
      setCurrentStep('payment-method');
    } catch (err: unknown) {
      console.error('Booking creation failed:', err);

      // Extract a user-friendly message
      let message = tr(
        'Something went wrong while creating your booking. Please try again.',
        'Si è verificato un errore durante la creazione della prenotazione. Riprova.'
      );

      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as { response?: { data?: { error?: string }; status?: number } }).response;
        if (response?.data?.error) {
          message = response.data.error;
        } else if (response?.status === 404) {
          message = tr(
            'The selected professional is not yet available for online booking. Please try another professional or contact them directly.',
            'Il professionista selezionato non è ancora disponibile per la prenotazione online. Prova un altro professionista o contattalo direttamente.'
          );
        }
      }

      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayNow = () => {
    setCurrentStep('payment-checkout');
  };

  const handlePayLater = () => {
    // Skip payment, go directly to confirmation
    setCurrentStep('confirmation');
  };

  const handlePaymentSuccess = () => {
    setCurrentStep('payment-success');
  };

  const handleRetryPayment = () => {
    setPaymentError('');
    setCurrentStep('payment-checkout');
  };

  const handleChangePaymentMethod = () => {
    setPaymentError('');
    setCurrentStep('payment-checkout');
  };

  const handleSwitchToPayLater = () => {
    setPaymentError('');
    setCurrentStep('confirmation');
  };

  const handleViewAppointment = () => {
    setCurrentStep('confirmation');
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'pets':
        return tr('Select Pet(s)', 'Seleziona animale/i');
      case 'category':
        return tr('Choose Service', 'Scegli servizio');
      case 'service-selection':
        return tr('Select Service', 'Seleziona servizio');
      case 'directory':
        return tr('Find Professional', 'Trova professionista');
      case 'service':
        return tr('Service Details', 'Dettagli servizio');
      case 'time':
        return tr('Select Date & Time', 'Seleziona data e ora');
      case 'data':
        return tr('Share Pet Information', 'Condividi informazioni animale');
      case 'summary':
        return tr('Review & Book', 'Rivedi e prenota');
      case 'payment-method':
        return tr('Payment Method', 'Metodo di pagamento');
      case 'payment-checkout':
        return tr('Secure Checkout', 'Checkout sicuro');
      case 'payment-success':
        return tr('Payment Successful', 'Pagamento riuscito');
      case 'payment-failure':
        return tr('Payment Failed', 'Pagamento non riuscito');
      case 'confirmation':
        return tr('Booking Confirmed', 'Prenotazione confermata');
      default:
        return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'pets':
        return bookingData.selectedPets.length > 0;
      case 'category':
        return bookingData.category !== null;
      case 'service-selection':
        return bookingData.selectedServices.length > 0;
      case 'directory':
        return bookingData.professional !== null;
      case 'service':
        return Object.keys(bookingData.serviceDetails).length > 0;
      case 'time':
        return bookingData.appointmentTime !== null;
      case 'data':
        return bookingData.sharedData.length > 0;
      case 'summary':
        return true;
      default:
        return false;
    }
  };

  // Payment flow screens
  if (currentStep === 'payment-method') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-[#16151D] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-[#16151D] border-b border-gray-200 dark:border-white/8 px-8 py-6 flex items-center justify-between">
            <div>
              <h2
                className="text-2xl mb-1 dark:text-white"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                  color: undefined,
                }}
              >
                {tr('Payment Method', 'Metodo di pagamento')}
              </h2>
              <p
                className="text-sm dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif', color: undefined }}
              >
                {tr("Choose how you'd like to pay", 'Scegli come vuoi pagare')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-white/70" />
            </button>
          </div>
          <div className="p-8">
            <PaymentMethodSelection
              onSelectPayNow={handlePayNow}
              onSelectPayLater={handlePayLater}
              totalAmount={totalAmount}
            />
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'payment-checkout') {
    return (
      <CardPaymentCheckout
        bookingData={bookingData}
        totalAmount={totalAmount}
        onSuccess={handlePaymentSuccess}
        onCancel={onClose}
        onBack={() => setCurrentStep('payment-method')}
      />
    );
  }

  if (currentStep === 'payment-success') {
    return (
      <PaymentSuccess
        bookingData={bookingData}
        totalAmount={totalAmount}
        onViewAppointment={handleViewAppointment}
        onClose={onClose}
      />
    );
  }

  if (currentStep === 'payment-failure') {
    return (
      <PaymentFailure
        errorReason={paymentError || 'card_declined'}
        onRetry={handleRetryPayment}
        onChangePaymentMethod={handleChangePaymentMethod}
        onSwitchToPayLater={handleSwitchToPayLater}
        onCancel={onClose}
      />
    );
  }

  if (currentStep === 'confirmation') {
    return <BookingConfirmation bookingData={bookingData} onClose={onClose} bookingId={createdBookingId || undefined} />;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl mb-1">
                {tr('Book Appointment', 'Prenota appuntamento')}
              </h2>

              <p className="text-sm opacity-90">{getStepTitle()}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {[
              tr('Pets', 'Animali'),
              tr('Service', 'Servizio'),
              tr('Professional', 'Professionista'),
              tr('Details', 'Dettagli'),
              tr('Time', 'Orario'),
              tr('Data', 'Dati'),
              tr('Review', 'Riepilogo'),
            ].map((step, index) => {
              const stepKeys = [
                'pets',
                'category',
                'service-selection',
                'directory',
                'service',
                'time',
                'data',
                'summary',
              ];
              const currentIndex = stepKeys.indexOf(currentStep);
              const isComplete = index < currentIndex;
              const isCurrent = index === currentIndex;

              return (
                <div key={index} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                        isComplete
                          ? 'bg-green-500'
                          : isCurrent
                            ? 'bg-white text-blue-600'
                            : 'bg-white/30'
                      }`}
                    >
                      {isComplete ? <Check className="w-4 h-4" /> : index + 1}
                    </div>
                    <span className="text-xs mt-1 hidden sm:block">{step}</span>
                  </div>
                  {index < 6 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${isComplete ? 'bg-green-500' : 'bg-white/30'}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 dark:bg-[#16151D]">
          {currentStep === 'pets' && (
            <PetSelection
              selectedPets={bookingData.selectedPets}
              onSelectionChange={(pets) =>
                updateBookingData('selectedPets', pets)
              }
            />
          )}

          {currentStep === 'category' && (
            <CategorySelection
              selectedCategory={bookingData.category}
              onCategoryChange={(category) =>
                updateBookingData('category', category)
              }
            />
          )}

          {currentStep === 'service-selection' && bookingData.category && (
            <ServiceSelection
              category={bookingData.category}
              selectedServices={bookingData.selectedServices}
              onServiceChange={(services) =>
                updateBookingData('selectedServices', services)
              }
            />
          )}

          {currentStep === 'directory' && bookingData.category && (
            <ProfessionalDirectory
              category={bookingData.category}
              selectedServices={bookingData.selectedServices}
              selectedProfessional={bookingData.professional}
              onProfessionalSelect={(professional) =>
                updateBookingData('professional', professional)
              }
              onNext={handleNext}
            />
          )}

          {currentStep === 'service' &&
            bookingData.category &&
            bookingData.professional && (
              <ServiceDetails
                category={bookingData.category}
                professional={bookingData.professional}
                serviceDetails={bookingData.serviceDetails}
                onDetailsChange={(details) =>
                  updateBookingData('serviceDetails', details)
                }
              />
            )}

          {currentStep === 'time' && (
            <TimeSelection
              professional={bookingData.professional}
              appointmentTime={bookingData.appointmentTime}
              onTimeSelect={(time) =>
                updateBookingData('appointmentTime', time)
              }
            />
          )}

          {currentStep === 'data' && bookingData.category && (
            <DataSharingConfirmation
              category={bookingData.category}
              professional={bookingData.professional}
              sharedData={bookingData.sharedData}
              accessDuration={bookingData.accessDuration}
              onDataChange={(data) => updateBookingData('sharedData', data)}
              onDurationChange={(duration) =>
                updateBookingData('accessDuration', duration)
              }
            />
          )}

          {currentStep === 'summary' && (
            <BookingSummary
              bookingData={bookingData}
              onComplete={handleComplete}
            />
          )}
        </div>

        {/* Footer */}
        {currentStep !== 'confirmation' && (
          <div className="border-t border-gray-200 dark:border-white/8 p-6 bg-gray-50 dark:bg-[#0F0E16]">
            <div className="flex justify-between">
              {currentStep !== 'pets' && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 dark:text-white"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {tr('Back', 'Indietro')}
                </button>
              )}

              <div className="ml-auto flex flex-col items-end gap-2">
                {submitError && currentStep === 'summary' && (
                  <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 max-w-md text-right">
                    {submitError}
                  </div>
                )}
                {currentStep !== 'summary' ? (
                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {tr('Continue', 'Continua')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleComplete}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {tr('Booking...', 'Prenotazione...')}
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        {tr('Book Appointment', 'Prenota appuntamento')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
